import React, { useState, useEffect, useRef } from 'react';
// import { Link } from 'react-router-dom';

const NeuralNetworkHerov2 = () => {
  const [exploded, setExploded] = useState(false);
  const [glowingNeurons, setGlowingNeurons] = useState({});
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [stars, setStars] = useState([]);
  
  const particlesRef = useRef([]);
  const requestRef = useRef();
  const pathUsageRef = useRef({});
  const starAnimationRef = useRef();
  
  const layers = [
    { name: "Input", neurons: 5, color: "rgba(99, 102, 241, 0.8)" },
    { name: "Hidden 1", neurons: 5, color: "rgba(139, 92, 246, 0.8)" },
    { name: "Hidden 2", neurons: 5, color: "rgba(236, 72, 153, 0.8)" },
    { name: "Hidden 3", neurons: 5, color: "rgba(249, 115, 22, 0.8)" },
    { name: "Output", neurons: 5, color: "rgba(244, 63, 94, 0.8)" }
  ];

  // Initialize window size, stars, and trigger explosion
  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    updateWindowSize();
    window.addEventListener('resize', updateWindowSize);
    
    // Generate stars once we know window size
    setTimeout(() => {
      generateStars();
      setExploded(true);
    }, 1000);
    
    return () => {
      window.removeEventListener('resize', updateWindowSize);
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      if (starAnimationRef.current) cancelAnimationFrame(starAnimationRef.current);
    };
  }, []);

    // Generate stars
    const generateStars = () => {
      // Use a fixed density of stars based on area
      // Use a larger viewport width than actual to ensure coverage during resizing
      const viewportWidth = Math.max(window.innerWidth * 1.5, 2500); // At least 2500px wide or 1.5x current width
      const viewportHeight = window.innerHeight;
      
      // Increase star density by reducing divisor
      const starCount = Math.min(500, Math.floor((viewportWidth * viewportHeight) / 1800));
      const newStars = [];
      
      for (let i = 0; i < starCount; i++) {
        const x = Math.random() * viewportWidth;
        const y = Math.random() * viewportHeight;
      // Reduce the base size range
      const size = Math.random() * 0.6 + 0.3; // 0.3 to 0.9px (smaller)
      
      // Increase percentage of twinkling stars
      const shouldTwinkle = Math.random() > 0.5;
      const twinkleSpeed = 0.5 + Math.random() * 1.5; // Random speed
      const twinklePhase = Math.random() * Math.PI * 2; // Random starting phase
      
      // Significantly increase the number of cross-stars (from 8% to 20%)
      const isCrossStar = Math.random() > 0.8;
      
      // Make more stars bigger but cap their max size
      const isBigStar = Math.random() > 0.92 && !isCrossStar;
      // Reduce the multiplier for big stars
      const specialSize = isBigStar ? size * (1.2 + Math.random() * 0.8) : size; // Max multiplier reduced from 4x to 2x
      
      newStars.push({
        id: `star-${i}`,
        x,
        y,
        size: specialSize,
        opacity: Math.random() * 0.5 + 0.5, // 0.5 to 1.0
        shouldTwinkle,
        twinkleSpeed,
        twinklePhase,
        color: getStarColor(),
        isBigStar,
        isDiamondStar: isCrossStar
      });
    }
    
    setStars(newStars);
    animateStars();
  };
  
  // Random star color function
  const getStarColor = () => {
    const colors = [
      "rgba(255, 255, 255, 1)", // White (most common)
      "rgba(200, 200, 255, 1)", // Bluish
      "rgba(255, 240, 200, 1)", // Yellowish
      "rgba(255, 255, 210, 1)",  // Light yellow for brighter stars
      "rgba(210, 255, 210, 1)"  // Light green tint
    ];
    
    // For diamond stars, prefer yellows (higher probability)
    if (Math.random() > 0.4) { // Increased from 0.7 to 0.4
      if (Math.random() > 0.5) {
        return "rgba(255, 255, 210, 1)"; // Yellow-white
      } else {
        return "rgba(255, 240, 180, 1)"; // Golden
      }
    }
    
    const randomIndex = Math.random();
    if (randomIndex > 0.8) {
      return colors[Math.floor(Math.random() * (colors.length - 1)) + 1];
    }
    return colors[0]; // White is most common
  };
  
  // Animate stars twinkling
  const animateStars = () => {
    const animate = () => {
      setStars(prevStars => prevStars.map(star => {
        if (!star.shouldTwinkle) return star;
        
        // Update twinkling based on time
        const time = performance.now() / 1000;
        const twinkling = 0.5 + 0.5 * Math.sin(time * star.twinkleSpeed + star.twinklePhase);
        
        return {
          ...star,
          currentOpacity: star.opacity * twinkling
        };
      }));
      
      starAnimationRef.current = requestAnimationFrame(animate);
    };
    
    starAnimationRef.current = requestAnimationFrame(animate);
  };

  // Watch for window size changes and update stars when needed
  useEffect(() => {
    // Only regenerate stars if we have a significant width increase
    if (stars.length > 0) {
      // Find the rightmost star's position
      const maxStarX = Math.max(...stars.map(star => star.x));
      
      // If the window is now wider than our stars coverage (with some margin)
      if (window.innerWidth > maxStarX + 20) {
        generateStars();
      }
    }
  }, [windowSize]);

  // KEY FIX: Update path points when window size changes
  useEffect(() => {
    if (!exploded || windowSize.width === 0) return;
    
    // Recalculate all stored path points based on new window size
    Object.keys(pathUsageRef.current).forEach(pathKey => {
      // Parse the path key to get layer and neuron info
      const [source, target] = pathKey.split('|');
      const [sourceLayerIndex, sourceNeuronIndex] = source.split('-').map(Number);
      const [targetLayerIndex, targetNeuronIndex] = target.split('-').map(Number);
      
      // Calculate new positions based on current window size
      const { x: sourceX, y: sourceY } = calculatePosition(sourceLayerIndex, sourceNeuronIndex);
      const { x: targetX, y: targetY } = calculatePosition(targetLayerIndex, targetNeuronIndex);
      
      // Control points for curve
      const controlPoint1X = sourceX + 60;
      const controlPoint1Y = sourceY;
      const controlPoint2X = targetX - 60;
      const controlPoint2Y = targetY;
      
      // Regenerate path points
      const pathPoints = [];
      const steps = 20; // More points for smoother curve
      
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const point = calculateBezierPoint(
          t,
          sourceX, sourceY,
          controlPoint1X, controlPoint1Y,
          controlPoint2X, controlPoint2Y,
          targetX, targetY
        );
        pathPoints.push(point);
      }
      
      // Update the stored path points
      if (pathUsageRef.current[pathKey]) {
        pathUsageRef.current[pathKey].points = pathPoints;
      }
    });
  }, [windowSize, exploded]); // Recalculate when window size changes

  // Start animation once exploded
  useEffect(() => {
    if (!exploded || windowSize.width === 0) return;
    
    initializeParticles();
    
    const animate = () => {
      updateParticles();
      requestRef.current = requestAnimationFrame(animate);
    };
    
    requestRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [exploded, windowSize]);

  // Calculate neuron position
  const calculatePosition = (layerIndex, neuronIndex) => {
    // Use fixed height for visualization instead of dynamic window height
    const visualHeight = 500; // Increased height to push nodes lower
    const marginX = 100;
    const marginY = 30; // Reduced top margin to push nodes lower
    
    const x = marginX + layerIndex * (windowSize.width - (marginX * 2)) / (layers.length - 1);
    const y = marginY + neuronIndex * (visualHeight - (marginY)) / (layers[layerIndex].neurons - 1);
    return { x, y };
  };

  // Initialize particles
  const initializeParticles = () => {
    const particles = [];
    const particleCount = 15; // Number of particles
    
    for (let i = 0; i < particleCount; i++) {
      const layerIndex = 0;
      const sourceNeuron = Math.floor(Math.random() * layers[layerIndex].neurons);
      const targetNeuron = Math.floor(Math.random() * layers[layerIndex + 1].neurons);
      
      const { x: sourceX, y: sourceY } = calculatePosition(layerIndex, sourceNeuron);
      const { x: targetX, y: targetY } = calculatePosition(layerIndex + 1, targetNeuron);
      
      particles.push({
        id: `particle-${i}`,
        sourceLayer: layerIndex,
        sourceNeuron,
        targetLayer: layerIndex + 1,
        targetNeuron,
        sourceX,
        sourceY,
        targetX,
        targetY,
        progress: Math.random() * 0.1, // Start near beginning
        duration: Math.random() * 3 + 2, // 2-5 seconds for movement
        lastUpdate: Date.now(),
        usedPaths: {},
        opacity: 1,
        // Trail properties
        trail: [] // Store points for the trailing effect
      });
    }
    
    particlesRef.current = particles;
  };
  
  // Path key helper
  const getPathKey = (sourceLayer, sourceNeuron, targetLayer, targetNeuron) => 
    `${sourceLayer}-${sourceNeuron}|${targetLayer}-${targetNeuron}`;
  
  // Update path usage count
  const updatePathUsage = (sourceLayer, sourceNeuron, targetLayer, targetNeuron) => {
    const pathKey = getPathKey(sourceLayer, sourceNeuron, targetLayer, targetNeuron);
    
    if (!pathUsageRef.current[pathKey]) {
      pathUsageRef.current[pathKey] = { 
        count: 1, 
        lastUsed: Date.now(),
        points: []
      };
    } else {
      pathUsageRef.current[pathKey].count++;
      pathUsageRef.current[pathKey].lastUsed = Date.now();
    }
    
    return pathUsageRef.current[pathKey].count;
  };
  
  // Calculate point on Bezier curve
  const calculateBezierPoint = (t, sx, sy, c1x, c1y, c2x, c2y, tx, ty) => {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    const t2 = t * t;
    const t3 = t2 * t;
    
    const x = mt3 * sx + 3 * mt2 * t * c1x + 3 * mt * t2 * c2x + t3 * tx;
    const y = mt3 * sy + 3 * mt2 * t * c1y + 3 * mt * t2 * c2y + t3 * ty;
    
    return { x, y };
  };
  
  // Main update function
  const updateParticles = () => {
    const now = Date.now();
    const updatedGlowing = { ...glowingNeurons };
    
    particlesRef.current = particlesRef.current.map(particle => {
      const { sourceLayer, targetLayer, duration, lastUpdate, progress } = particle;
      
      // Calculate elapsed time in seconds
      const elapsed = (now - lastUpdate) / 1000;
      
      // Calculate positions based on current window size
      const { x: sourceX, y: sourceY } = calculatePosition(sourceLayer, particle.sourceNeuron);
      const { x: targetX, y: targetY } = calculatePosition(targetLayer, particle.targetNeuron);
      
      // Update progress
      const newProgress = progress + (elapsed / duration);
      
      // Control points for curve
      const controlPoint1X = sourceX + 60;
      const controlPoint1Y = sourceY;
      const controlPoint2X = targetX - 60;
      const controlPoint2Y = targetY;
      
      // If particle reached target
      if (newProgress >= 1) {
        // Update path usage
        const usageCount = updatePathUsage(
          sourceLayer, particle.sourceNeuron, targetLayer, particle.targetNeuron
        );
        
        const pathKey = getPathKey(
          sourceLayer, particle.sourceNeuron, targetLayer, particle.targetNeuron
        );
        
        const updatedUsedPaths = { 
          ...particle.usedPaths,
          [pathKey]: (particle.usedPaths[pathKey] || 0) + 1 
        };
        
        // Store points for this path if not already stored
        if (pathUsageRef.current[pathKey] && 
            (!pathUsageRef.current[pathKey].points || 
             pathUsageRef.current[pathKey].points.length === 0)) {
          
          const pathPoints = [];
          const steps = 20; // More points for smoother curve
          
          for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const point = calculateBezierPoint(
              t,
              sourceX, sourceY,
              controlPoint1X, controlPoint1Y,
              controlPoint2X, controlPoint2Y,
              targetX, targetY
            );
            pathPoints.push(point);
          }
          
          pathUsageRef.current[pathKey].points = pathPoints;
        }
        
        // Make target neuron glow
        const neuronKey = `${targetLayer}-${particle.targetNeuron}`;
        const glowDuration = 800 + Math.min(2000, usageCount * 200);
        updatedGlowing[neuronKey] = now + glowDuration;
        
        // Determine next target
        let newSourceLayer, newSourceNeuron, newTargetLayer, newTargetNeuron;
        
        if (targetLayer >= layers.length - 1) {
          // Reset to input layer
          newSourceLayer = 0;
          newSourceNeuron = Math.floor(Math.random() * layers[0].neurons);
          newTargetLayer = 1;
          newTargetNeuron = Math.floor(Math.random() * layers[1].neurons);
        } else {
          // Continue to next layer
          newSourceLayer = targetLayer;
          newSourceNeuron = particle.targetNeuron;
          newTargetLayer = targetLayer + 1;
          
          // Choose target node - prefer less traveled paths
          if (Math.random() < 0.7) {
            let minUsage = Infinity;
            let candidates = [];
            
            for (let i = 0; i < layers[newTargetLayer].neurons; i++) {
              const usage = pathUsageRef.current[getPathKey(newSourceLayer, newSourceNeuron, newTargetLayer, i)]?.count || 0;
              
              if (usage < minUsage) {
                minUsage = usage;
                candidates = [i];
              } else if (usage === minUsage) {
                candidates.push(i);
              }
            }
            
            newTargetNeuron = candidates[Math.floor(Math.random() * candidates.length)];
          } else {
            newTargetNeuron = Math.floor(Math.random() * layers[newTargetLayer].neurons);
          }
        }
        
        // Calculate new positions
        const { x: newSourceX, y: newSourceY } = calculatePosition(newSourceLayer, newSourceNeuron);
        const { x: newTargetX, y: newTargetY } = calculatePosition(newTargetLayer, newTargetNeuron);
        
        return {
          ...particle,
          sourceLayer: newSourceLayer,
          sourceNeuron: newSourceNeuron,
          targetLayer: newTargetLayer,
          targetNeuron: newTargetNeuron,
          sourceX: newSourceX,
          sourceY: newSourceY,
          targetX: newTargetX,
          targetY: newTargetY,
          progress: 0,
          lastUpdate: now,
          usedPaths: updatedUsedPaths,
          trail: [] // Reset trail for new path
        };
      }
      
      // Calculate current position along the curve
      const currentPoint = calculateBezierPoint(
        newProgress,
        sourceX, sourceY,
        controlPoint1X, controlPoint1Y,
        controlPoint2X, controlPoint2Y,
        targetX, targetY
      );
      
      // Update trail for shooting star effect
      const trail = [...particle.trail];
      
      // Add current point to trail
      trail.push({ x: currentPoint.x, y: currentPoint.y, progress: newProgress });
      
      // Keep only recent points for the trail
      const maxTrailLength = 10;
      if (trail.length > maxTrailLength) {
        trail.splice(0, trail.length - maxTrailLength);
      }
      
      return {
        ...particle,
        progress: newProgress,
        lastUpdate: now,
        sourceX,
        sourceY,
        targetX,
        targetY,
        trail
      };
    });
    
    // Clean up expired glows
    Object.keys(updatedGlowing).forEach(key => {
      if (updatedGlowing[key] < now) delete updatedGlowing[key];
    });
    
    setGlowingNeurons(updatedGlowing);
  };
  
  // Generate SVG path from points
  const generatePath = (points) => {
    if (!points || points.length < 2) return '';
    
    return points.reduce((path, point, i) => 
      i === 0 ? `M${point.x},${point.y}` : `${path} L${point.x},${point.y}`
    , '');
  };
  
  // RENDER FUNCTIONS
  
  // Render stars
  const renderStars = () => {
    return stars.map(star => {
      const opacity = star.currentOpacity !== undefined ? star.currentOpacity : star.opacity;
      
      // Special glow for big stars
      const hasGlow = star.isBigStar;
      
      if (star.isDiamondStar) {
        // Create a four-pointed star shape like the reference image
        // Make the diamond stars bigger overall
        const diamondSize = star.size * (star.shouldTwinkle ? 4 : 3); // Increased size multipliers
        const pulseScale = star.shouldTwinkle ? 
          1 + (0.3 * Math.sin(performance.now() / 500 * star.twinkleSpeed + star.twinklePhase)) : 1;
        
        // Get color with alpha adjusted
        const baseColor = star.color.replace('1)', '');
        
        return (
          <g key={star.id} transform={`translate(${star.x}, ${star.y}) scale(${pulseScale})`}>
            {/* Create the four-pointed star effect */}
            <defs>
              <filter id={`glow-${star.id}`} x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="3" result="blur"/>
                <feColorMatrix in="blur" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 5 0"/>
                <feComposite in="SourceGraphic" in2="blur" operator="over"/>
              </filter>
            </defs>
            
            {/* Outer cross glow */}
            <path 
              d={`M0,${-diamondSize * 2} L0,${diamondSize * 2} M${-diamondSize * 2},0 L${diamondSize * 2},0`} 
              stroke={`${baseColor}0.15)`}
              strokeWidth={diamondSize * 0.5}
              opacity={opacity * 0.6}
              filter={`url(#glow-${star.id})`}
            />
            
            {/* Middle cross glow */}
            <path 
              d={`M0,${-diamondSize * 1.5} L0,${diamondSize * 1.5} M${-diamondSize * 1.5},0 L${diamondSize * 1.5},0`} 
              stroke={`${baseColor}0.4)`}
              strokeWidth={diamondSize * 0.4}
              opacity={opacity * 0.8}
            />
            
            {/* Inner cross - brighter */}
            <path 
              d={`M0,${-diamondSize} L0,${diamondSize} M${-diamondSize},0 L${diamondSize},0`} 
              stroke={`${baseColor}0.9)`}
              strokeWidth={diamondSize * 0.2}
              opacity={opacity}
            />
            
            {/* Center dot */}
            <circle 
              cx="0" 
              cy="0" 
              r={diamondSize * 0.2} 
              fill={`${baseColor}1)`} 
              opacity={opacity}
            />
          </g>
        );
      }
      
      return (
        <g key={star.id}>
          {hasGlow && (
            <circle
              cx={star.x}
              cy={star.y}
              r={star.size * 1.5} // Reduced glow radius
              fill="none"
              stroke="rgba(255, 255, 255, 0.08)" // Reduced opacity
              strokeWidth={0.4} // Thinner stroke
              opacity={opacity * 0.4} // Reduced opacity
            />
          )}
          <circle
            cx={star.x}
            cy={star.y}
            r={star.size}
            fill={star.color}
            opacity={opacity}
            filter={star.isBigStar ? "url(#star-glow)" : ""}
          />
        </g>
      );
    });
  };
  
  // Render permanent connection paths
  const renderConnections = () => {
    const connections = [];
    
    Object.keys(pathUsageRef.current).forEach(pathKey => {
      const usageData = pathUsageRef.current[pathKey];
      if (!usageData || usageData.count === 0) return;
      
      const usageCount = usageData.count;
      
      // Style based on usage - INCREASED OPACITY for better visibility
      const lineOpacity = 0.15 + Math.min(0.5, usageCount * 0.05);
      const lineWidth = 0.5 + Math.min(2.5, usageCount * 0.25);
      
      // Color based on usage - brighter colors
      let strokeColor;
      if (usageCount >= 10) {
        strokeColor = "rgba(120, 170, 255, 0.8)";
      } else if (usageCount >= 5) {
        strokeColor = "rgba(100, 140, 240, 0.7)";
      } else if (usageCount >= 2) {
        strokeColor = "rgba(80, 110, 220, 0.6)";
      } else {
        strokeColor = "rgba(148, 163, 184, 0.5)";
      }
      
      // If we have pre-calculated points, use them
      if (usageData.points && usageData.points.length > 0) {
        connections.push(
          <path
            key={pathKey}
            d={generatePath(usageData.points)}
            stroke={strokeColor}
            strokeWidth={lineWidth}
            opacity={lineOpacity}
            fill="none"
            className="transition-all duration-500 ease-out"
          />
        );
      } else {
        // Parse the path key to get layer and neuron info
        const [source, target] = pathKey.split('|');
        const [sourceLayerIndex, sourceNeuronIndex] = source.split('-').map(Number);
        const [targetLayerIndex, targetNeuronIndex] = target.split('-').map(Number);
        
        // Calculate positions
        const { x: sourceX, y: sourceY } = calculatePosition(sourceLayerIndex, sourceNeuronIndex);
        const { x: targetX, y: targetY } = calculatePosition(targetLayerIndex, targetNeuronIndex);
        
        connections.push(
          <path
            key={pathKey}
            d={`M${sourceX} ${sourceY} C${sourceX + 60} ${sourceY}, ${targetX - 60} ${targetY}, ${targetX} ${targetY}`}
            stroke={strokeColor}
            strokeWidth={lineWidth}
            opacity={lineOpacity}
            fill="none"
            className="transition-all duration-500 ease-out"
          />
        );
      }
    });
    
    return connections;
  };

  // Render shooting star trails
  const renderShootingStars = () => {
    if (!exploded) return null;
    
    return particlesRef.current.map(particle => {
      if (particle.trail.length < 2) return null;
      
      const { sourceLayer, targetLayer, progress } = particle;
      
      // Get colors for gradient
      const sourceColor = layers[sourceLayer].color.replace('0.8', '0.2');
      const targetColor = layers[targetLayer].color.replace('0.8', '0.9');
      
      // Create unique gradient ID
      const gradientId = `trail-gradient-${particle.id}`;
      
      // Calculate trail path
      const pathPoints = [...particle.trail];
      const pathStr = generatePath(pathPoints);
      
      return (
        <g key={`trail-${particle.id}`}>
          <defs>
            <linearGradient
              id={gradientId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor={sourceColor} stopOpacity="0.1" />
              <stop offset="100%" stopColor={targetColor} stopOpacity="0.9" />
            </linearGradient>
          </defs>
          
          <path
            d={pathStr}
            stroke={`url(#${gradientId})`}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            className="shooting-star-trail"
            filter="url(#glow)"
          />
        </g>
      );
    }).filter(Boolean);
  };

  // Render neurons
  const renderNeurons = () => {
    const neurons = [];
    
    layers.forEach((layer, layerIndex) => {
      for (let i = 0; i < layer.neurons; i++) {
        // Get position from our position calculator
        const { x, y } = calculatePosition(layerIndex, i);
        
        const neuronKey = `${layerIndex}-${i}`;
        const isGlowing = glowingNeurons[neuronKey] !== undefined;
        const baseSize = 6; // Slightly larger size for better visibility
        
        neurons.push(
          <g 
            key={neuronKey}
            className="transition-all duration-2000 ease-in-out"
            style={{
              transform: `translate(${x}px, ${y}px)`,
              opacity: exploded ? 1 : 0.3
            }}
          >
            {isGlowing && (
              <>
                <circle
                  r={baseSize * 3}
                  fill="none"
                  stroke={layer.color.replace('0.8', '0.15')}
                  strokeWidth={3}
                  opacity={0.9}
                  className="animate-burst-slow"
                />
                <circle
                  r={baseSize * 2}
                  fill={layer.color.replace('0.8', '0.2')}
                  stroke="none"
                  opacity={0.5}
                  className="animate-burst-medium"
                />
              </>
            )}
            
            <circle 
              r={isGlowing ? baseSize * 1.4 : baseSize}
              fill={isGlowing ? layer.color.replace('0.8', '0.95') : layer.color}
              stroke={isGlowing ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.6)"}
              strokeWidth={isGlowing ? 1.5 : 1}
              className={isGlowing ? "neuron-jiggle" : ""}
            />
          </g>
        );
      }
    });
    
    return neurons;
  };

  // Render particles
  const renderParticles = () => {
    if (!exploded) return null;
    
    return particlesRef.current.map(particle => {
      const { id, sourceLayer, targetLayer, progress } = particle;
      
      // Get the current position from the trail's last point
      const trailLength = particle.trail.length;
      if (trailLength === 0) return null;
      
      const position = particle.trail[trailLength - 1];
      
      // Determine particle color
      const sourceColor = layers[sourceLayer].color;
      const targetColor = layers[targetLayer].color;
      const particleColor = progress < 0.5 ? sourceColor : targetColor;
      
      // Size based on paths traveled
      const pathsTraveled = Object.keys(particle.usedPaths || {}).length;
      const size = Math.min(2.5, 1.5 + pathsTraveled * 0.2);
      
      return (
        <g key={id}>
          {/* Glow effect */}
          <circle
            cx={position.x}
            cy={position.y}
            r={size * 1.8}
            fill={particleColor.replace('0.8', '0.3')}
            opacity={0.5}
            filter="url(#glow)"
          />
          
          {/* Main particle */}
          <circle
            cx={position.x}
            cy={position.y}
            r={size}
            fill={particleColor.replace('0.8', '0.95')}
            className="particle-core"
            filter="url(#particle-glow)"
          />
        </g>
      );
    }).filter(Boolean);
  };

  return (
    <div className="relative w-full h-[550px]">
      <div className="absolute inset-0 overflow-hidden bg-black">
        <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255, 255, 255, 0.02)" strokeWidth="1"/>
            </pattern>
            
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.5" result="blur"/>
              <feComposite in="SourceGraphic" in2="blur" operator="over"/>
            </filter>
            
            <filter id="particle-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.5" result="blur"/>
              <feComposite in="SourceGraphic" in2="blur" operator="over"/>
            </filter>
            
            <filter id="star-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1" result="blur"/>
              <feComposite in="SourceGraphic" in2="blur" operator="over"/>
            </filter>
            
            <filter id="diamond-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.5" result="blur"/>
              <feComposite in="SourceGraphic" in2="blur" operator="over"/>
              <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 3 0" result="brightened"/>
            </filter>
            
            <radialGradient id="star-big-glow" cx="0.5" cy="0.5" r="0.5" fx="0.5" fy="0.5">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.8)" />
              <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
            </radialGradient>
          </defs>
          
          {/* Background */}
          <rect width="100%" height="100%" fill="rgb(0, 0, 12)" />
          
          {/* Grid pattern overlay */}
          <rect width="100%" height="100%" fill="url(#grid)" />
          
          {/* Stars background layer - drawn in a wider area than visible viewport */}
          <g className="stars-container">
            {renderStars()}
          </g>
          
          {/* Regular connection paths (background) */}
          {renderConnections()}
          
          {/* Shooting star trails */}
          {renderShootingStars()}
          
          {/* Neurons */}
          {renderNeurons()}
          
          {/* Particles with glow */}
          {renderParticles()}
        </svg>
      </div>
      
      {/* Comment out this entire div block */}
      {/*
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-white px-3 pt-0 pb-20">
        <div className="text-center px-6 py-4 rounded-xl bg-black/40 backdrop-blur-sm">
          <h1 className="text-4xl md:text-5xl font-light tracking-tight mb-3 text-white drop-shadow-lg">
            Build Anything with AI
          </h1>
          <p className="text-lg text-gray-300 font-light leading-relaxed max-w-3xl mx-auto mb-3 drop-shadow">
            The visual programming platform where LLMs help you orchestrate AI models into powerful applications.
          </p>
          <Link 
            to="/join-waitlist" 
            className="mt-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white 
                      font-medium rounded-md transition-colors duration-300 shadow-lg inline-block">
            Join Waitlist
          </Link>
        </div>
      </div>
      */}
      
      <style jsx>{`
      @keyframes burst-slow {
        0% { transform: scale(0.5); opacity: 0.7; }
        40% { transform: scale(1.2); opacity: 0.5; }
        100% { transform: scale(2); opacity: 0; }
      }
      
      @keyframes burst-medium {
        0% { transform: scale(0.2); opacity: 0.8; }
        60% { transform: scale(1.5); opacity: 0.3; }
        100% { transform: scale(1.8); opacity: 0; }
      }
      
      @keyframes twinkle {
        0% { opacity: 0.2; }
        50% { opacity: 1; }
        100% { opacity: 0.2; }
      }
      
      @keyframes diamond-pulse {
        0% { transform: scale(0.85); opacity: 0.7; }
        50% { transform: scale(1.15); opacity: 1; }
        100% { transform: scale(0.85); opacity: 0.7; }
      }
      
      .animate-burst-slow {
        animation: burst-slow 1.5s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
      }
      
      .diamond-pulse {
        animation: diamond-pulse 3s ease-in-out infinite;
      }
      
      .animate-burst-medium {
        animation: burst-medium 1.2s cubic-bezier(0, 0, 0.2, 1) forwards;
      }
      
      .star-twinkle {
        animation: twinkle 3s ease-in-out infinite;
      }
      
      .star-twinkle-slow {
        animation: twinkle 5s ease-in-out infinite;
      }
      
      .star-twinkle-fast {
        animation: twinkle 2s ease-in-out infinite;
      }
      
      .neuron-jiggle {
        animation: jiggle 0.4s ease-in-out;
      }
      
      @keyframes jiggle {
        0% { transform: scale(1); }
        30% { transform: scale(1.3); }
        50% { transform: scale(0.9); }
        70% { transform: scale(1.1); }
        100% { transform: scale(1); }
      }
      
      .particle-core {
        filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.7));
      }
      
      .shooting-star-trail {
        filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.4));
      }
      
      /* Add this new class at the end */
      .stars-container {
        overflow: visible; /* Allow stars to be visible outside the SVG viewport */
      }
    `}</style>
        </div>
      );
    };

    export default NeuralNetworkHerov2;